/**
 * Strategy Rules panel — shared component (Task 259; extracted from
 * StrategyPage, Tasks 251/258).
 *
 * Product placement (Task 259): the PRIMARY derive/review surface lives in the
 * client CREATIVE INTELLIGENCE workflow (stage 5 — strategy selection), after
 * Analysis: the user selects an existing active strategy/version there and
 * derives Strategy Rules from Creative Analysis + performance evidence. The
 * client Strategy page keeps a READ-ONLY status view with a link to that
 * workflow.
 *
 *   - variant="manage"   (Creative Intelligence): derive via the LIVE
 *     `derive-strategy-rules` edge function (house kmiFetch path, Task 258) +
 *     approve/dismiss via the prod-live RPCs. Leadership-gated by the caller.
 *   - variant="readonly" (Strategy page): list/status only — NO derive, NO
 *     approve/dismiss; an optional link to the manage surface.
 *
 * DELIBERATE, LOCALIZED deviation (extends the Task 245-B precedent), scoped
 * to REVIEW + LIST only: `supabase.rpc(...)` for the two prod-live lifecycle
 * RPCs (`approve` / `dismiss`), and a `supabase.from('strategy_rules')` SELECT
 * for the list (no read RPC or edge function exists; the strategy_rules RLS
 * SELECT policy gates the read). Do NOT generalize this; every other data call
 * in the app MUST keep using kmiFetch.
 *
 * Display (Task 259 scope 6): rules render GROUPED BY FAMILY with Arabic
 * family labels — never a raw mixed bullet dump — and each rule-set shows a
 * separate evidence summary (counts by evidence source, Arabic labels).
 *
 * No AI, no model call. It writes ONLY strategy_rules rows and touches no
 * other tenant surface. The backend is the source of truth — derive refuses
 * (409 `conflict`) when a live rule-set already exists, and approve/dismiss
 * are leadership-gated server-side; the FE renders a clean message in each
 * case.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isApiError, kmiFetch } from '../../api/client';
import { supabase } from '../../auth/supabase';
import { EmptyState } from '../../components/states';

export const STRATEGY_RULE_FAMILY_KEYS = [
  'hooks_to_repeat',
  'hooks_to_avoid',
  'cta_rules',
  'angle_rules',
  'platform_guidance',
  'audience_tone_rules',
  'fatigue_warnings',
  'format_recommendations',
] as const;

export type StrategyRuleFamilyKey = (typeof STRATEGY_RULE_FAMILY_KEYS)[number];

export const STRATEGY_RULE_FAMILY_LABEL_AR: Record<StrategyRuleFamilyKey, string> = {
  hooks_to_repeat: 'خطافات للتكرار',
  hooks_to_avoid: 'خطافات للتجنب',
  cta_rules: 'قواعد الحث على الإجراء',
  angle_rules: 'زوايا المحتوى',
  platform_guidance: 'إرشادات المنصات',
  audience_tone_rules: 'الجمهور والنبرة',
  fatigue_warnings: 'تحذيرات الإجهاد',
  format_recommendations: 'توصيات الصيغ',
};

/** Arabic labels for the evidence sources (Slice-2 contract vocabulary). */
const STRATEGY_RULE_EVIDENCE_SOURCE_AR: Record<string, string> = {
  creative_analysis: 'تحليل إبداعي',
  paid_media_metric: 'مقاييس إعلانية',
  creative_pattern: 'نمط إبداعي',
  strategy_version: 'نسخة الاستراتيجية',
};

type StrategyRuleStatus = 'suggested' | 'approved' | 'edited' | 'dismissed';

interface StrategyRulesRow {
  readonly id: string;
  readonly status: StrategyRuleStatus;
  readonly rules: Record<string, unknown>;
  readonly confidence: number | null;
  readonly strategy_version_id: string;
  readonly created_at: string;
  readonly reviewed_at: string | null;
}

const STRATEGY_RULE_STATUS_LABEL_AR: Record<StrategyRuleStatus, string> = {
  suggested: 'مقترحة — بانتظار المراجعة',
  approved: 'معتمدة — نشطة',
  edited: 'معدّلة',
  dismissed: 'مرفوضة',
};

/** Wire shape returned by the `derive-strategy-rules` edge function (Task 256). */
interface StrategyRulesDeriveSummary {
  readonly families: Readonly<Record<string, number>>;
  readonly analyzed_creatives: number;
  readonly skipped_analyses: number;
  readonly performance_groups: number;
  readonly matched_assets: number;
  readonly insight_rows: number;
  readonly partial: boolean;
}

interface StrategyRulesDeriveResponse {
  readonly strategy_rule_id: string;
  readonly rule_status: string;
  readonly strategy_version_id: string;
  readonly window: { readonly date_from: string; readonly date_to: string };
  readonly summary: StrategyRulesDeriveSummary;
}

interface FamilyGroup {
  readonly key: StrategyRuleFamilyKey;
  readonly label: string;
  readonly statements: ReadonlyArray<string>;
}

/** Group a rule bundle's statements BY FAMILY (Task 259 — no mixed dump). */
export function strategyRuleFamilyGroups(rules: Record<string, unknown>): FamilyGroup[] {
  const out: FamilyGroup[] = [];
  for (const key of STRATEGY_RULE_FAMILY_KEYS) {
    const arr = rules[key];
    if (!Array.isArray(arr)) continue;
    const statements: string[] = [];
    for (const item of arr) {
      const statement = (item as { statement?: unknown } | null)?.statement;
      if (typeof statement === 'string' && statement.length > 0) statements.push(statement);
    }
    if (statements.length > 0) {
      out.push({ key, label: STRATEGY_RULE_FAMILY_LABEL_AR[key], statements });
    }
  }
  return out;
}

/**
 * Evidence summary for a rule bundle: counts by evidence source, rendered
 * separately from the rule statements (Task 259 scope 6). Null when the
 * bundle carries no evidence at all.
 */
export function strategyRuleEvidenceSummaryAr(rules: Record<string, unknown>): string | null {
  const counts = new Map<string, number>();
  for (const key of STRATEGY_RULE_FAMILY_KEYS) {
    const arr = rules[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      const evidence = (item as { evidence?: unknown } | null)?.evidence;
      if (!Array.isArray(evidence)) continue;
      for (const ev of evidence) {
        const source = (ev as { source?: unknown } | null)?.source;
        if (typeof source === 'string' && source.length > 0) {
          counts.set(source, (counts.get(source) ?? 0) + 1);
        }
      }
    }
  }
  if (counts.size === 0) return null;
  const parts: string[] = [];
  for (const [source, n] of counts) {
    parts.push(`${STRATEGY_RULE_EVIDENCE_SOURCE_AR[source] ?? source} ×${n}`);
  }
  return `الأدلة: ${parts.join(' · ')}`;
}

/** Map a backend SQLSTATE (approve/dismiss direct RPCs) to clean Arabic copy.
 *  Backend remains the source of truth. */
function strategyRulesErrorAr(code: string | undefined): string {
  if (code === '42501') return 'لا تملك صلاحية تنفيذ هذا الإجراء.';
  if (code === '23503') return 'العنصر غير متاح.';
  if (code === '23514') return 'لا يمكن تنفيذ هذا الإجراء على الحالة الحالية.';
  return 'تعذّر تنفيذ الإجراء. حاول مجدداً.';
}

/** Map a `derive-strategy-rules` edge-function failure (KmiApiError wire codes,
 *  Task 258) to clean Arabic copy. The live-head refusal arrives as `conflict`
 *  (HTTP 409). */
function strategyRulesDeriveErrorAr(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'conflict')
      return 'توجد مجموعة قواعد نشطة بالفعل لهذه النسخة من الاستراتيجية. ارفضها أولاً قبل اشتقاق مجموعة جديدة.';
    if (err.code === 'permission_denied') return 'لا تملك صلاحية تنفيذ هذا الإجراء.';
    if (err.code === 'not_found') return 'العنصر غير متاح.';
    if (err.code === 'validation_error')
      return 'الاستراتيجية غير جاهزة — تأكد من وجود نسخة نشطة وأعمدة محتوى.';
  }
  return 'تعذّر اشتقاق القواعد. حاول مجدداً.';
}

type StrategyRulesListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly rows: ReadonlyArray<StrategyRulesRow> }
  | { readonly kind: 'error'; readonly message: string };

export function StrategyRulesPanel({
  clientId,
  strategyId,
  variant,
  canManage,
  canDerive,
  manageHref,
}: {
  readonly clientId: string;
  readonly strategyId: string;
  /** 'manage' = Creative Intelligence (derive + approve/dismiss); 'readonly' =
   *  Strategy page status view (no actions, optional link to manage). */
  readonly variant: 'manage' | 'readonly';
  readonly canManage: boolean;
  readonly canDerive: boolean;
  /** Where the manage surface lives (readonly variant renders a link to it). */
  readonly manageHref?: string;
}) {
  const isManage = variant === 'manage';
  const [listState, setListState] = useState<StrategyRulesListState>({ kind: 'loading' });
  // `busy` is 'derive' or a rule id while an action is in flight — disables every
  // button so nothing double-fires.
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Task 258 — the EF's derive summary (evidence counts + family counts),
  // rendered after a successful derive; cleared when a new action starts.
  const [deriveSummary, setDeriveSummary] = useState<StrategyRulesDeriveSummary | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setListState({ kind: 'loading' });
    void supabase
      .from('strategy_rules')
      .select('id, status, rules, confidence, strategy_version_id, created_at, reviewed_at')
      .eq('strategy_id', strategyId)
      .order('created_at', { ascending: false })
      .then(({ data, error }: { data: unknown; error: unknown }) => {
        if (cancelled) return;
        if (error) {
          setListState({ kind: 'error', message: 'تعذّر تحميل قواعد الاستراتيجية.' });
          return;
        }
        setListState({ kind: 'loaded', rows: (data ?? []) as ReadonlyArray<StrategyRulesRow> });
      });
    return () => {
      cancelled = true;
    };
  }, [strategyId, reloadKey]);

  async function onDerive(): Promise<void> {
    if (!isManage || busy !== null) return;
    setBusy('derive');
    setActionError(null);
    setDeriveSummary(null);
    // Task 258 — the LIVE edge function (house kmiFetch path). It assembles
    // real Creative Analysis + paid-media evidence server-side, runs the
    // deterministic pipeline (no AI), and persists ONE `suggested` rule-set.
    try {
      const data = await kmiFetch<StrategyRulesDeriveResponse>('derive-strategy-rules', {
        method: 'POST',
        body: { client_id: clientId, strategy_id: strategyId },
      });
      setBusy(null);
      setDeriveSummary(data.summary);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setBusy(null);
      setActionError(strategyRulesDeriveErrorAr(err));
    }
  }

  async function onReview(ruleId: string, action: 'approve' | 'dismiss'): Promise<void> {
    if (!isManage || busy !== null) return;
    setBusy(ruleId);
    setActionError(null);
    setDeriveSummary(null);
    const fn = action === 'approve' ? 'approve_strategy_rules_v1' : 'dismiss_strategy_rules_v1';
    const { error } = await supabase.rpc(fn, { p_rule_id: ruleId });
    setBusy(null);
    if (error) {
      setActionError(strategyRulesErrorAr((error as { code?: string }).code));
      return;
    }
    setReloadKey((n) => n + 1);
  }

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>قواعد الاستراتيجية</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="meta">strategy_rules · مراجعة بشرية</span>
          {isManage && canDerive ? (
            <button
              type="button"
              className="btn-text"
              disabled={busy !== null}
              onClick={() => {
                void onDerive();
              }}
              style={{ padding: '6px 10px', fontSize: 12 }}
              title="يشتق مجموعة قواعد مقترحة من نتائج تحليل الإبداعات وأداء الإعلانات (بدون ذكاء اصطناعي)"
            >
              {busy === 'derive' ? 'جاري الاشتقاق…' : 'اشتقاق قواعد مقترحة'}
            </button>
          ) : null}
          {!isManage && manageHref && canManage ? (
            <Link
              className="btn-text"
              to={manageHref}
              style={{ padding: '6px 10px', fontSize: 12 }}
            >
              إدارة القواعد من الذكاء الإبداعي ←
            </Link>
          ) : null}
        </div>
      </div>

      {!isManage ? (
        <p className="tiny muted" style={{ marginTop: 0 }}>
          عرض للاطلاع فقط — الاشتقاق والمراجعة يتمان من صفحة الذكاء الإبداعي (بعد التحليل واختيار
          الاستراتيجية).
        </p>
      ) : null}

      {actionError ? (
        <p className="tiny" role="alert" style={{ color: 'var(--rose)', marginTop: 0 }}>
          {actionError}
        </p>
      ) : null}

      {deriveSummary ? (
        <div className="tiny" role="status" style={{ marginTop: 0 }}>
          <p style={{ margin: '0 0 4px' }}>
            تم إنشاء مجموعة مقترحة من الأدلة الفعلية — التحليلات: {deriveSummary.analyzed_creatives}
            {' · '}مجموعات الأداء: {deriveSummary.performance_groups}
            {' · '}الأصول المطابقة: {deriveSummary.matched_assets}
            {' · '}صفوف البيانات: {deriveSummary.insight_rows}
          </p>
          <p className="muted" style={{ margin: 0 }}>
            {STRATEGY_RULE_FAMILY_KEYS.filter((k) => (deriveSummary.families[k] ?? 0) > 0).length >
            0
              ? STRATEGY_RULE_FAMILY_KEYS.filter((k) => (deriveSummary.families[k] ?? 0) > 0)
                  .map((k) => `${STRATEGY_RULE_FAMILY_LABEL_AR[k]}: ${deriveSummary.families[k]}`)
                  .join(' · ')
              : 'لا توجد قواعد ضمن العائلات بعد — الأدلة الحالية غير كافية.'}
          </p>
        </div>
      ) : null}

      {listState.kind === 'loading' ? (
        <p className="muted" style={{ margin: 0 }}>
          جاري تحميل القواعد…
        </p>
      ) : listState.kind === 'error' ? (
        <p className="tiny" role="alert" style={{ color: 'var(--rose)', margin: 0 }}>
          {listState.message}
        </p>
      ) : listState.rows.length === 0 ? (
        <EmptyState
          title="لا توجد قواعد بعد"
          description={
            isManage && canDerive
              ? 'اضغط «اشتقاق قواعد مقترحة» لإنشاء مجموعة للمراجعة.'
              : 'لم تُشتق أي قواعد لهذه الاستراتيجية بعد.'
          }
        />
      ) : (
        <div className="stack-sm" role="list">
          {listState.rows.map((row) => {
            const groups = strategyRuleFamilyGroups(row.rules);
            const evidenceSummary = strategyRuleEvidenceSummaryAr(row.rules);
            return (
              <div key={row.id} className="card" role="listitem" style={{ padding: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <strong>{STRATEGY_RULE_STATUS_LABEL_AR[row.status] ?? row.status}</strong>
                  <span className="meta">
                    {new Date(row.created_at).toLocaleDateString('ar-SA')}
                  </span>
                </div>
                {groups.length > 0 ? (
                  <div className="stack-sm" style={{ marginTop: 8 }}>
                    {groups.map((group) => (
                      <div key={group.key}>
                        <p className="tiny" style={{ margin: '0 0 4px', fontWeight: 600 }}>
                          {group.label} ({group.statements.length})
                        </p>
                        <ul className="strategy-bullets" style={{ margin: 0 }}>
                          {group.statements.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="muted" style={{ margin: '8px 0 0' }}>
                    لا توجد قواعد ضمن هذه المجموعة.
                  </p>
                )}
                {evidenceSummary ? (
                  <p className="tiny muted" style={{ margin: '8px 0 0' }}>
                    {evidenceSummary}
                  </p>
                ) : null}
                {isManage && canManage && row.status === 'suggested' ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn-text"
                      disabled={busy !== null}
                      onClick={() => {
                        void onReview(row.id, 'approve');
                      }}
                      style={{ padding: '6px 10px', fontSize: 12 }}
                    >
                      {busy === row.id ? 'جاري…' : 'اعتماد'}
                    </button>
                    <button
                      type="button"
                      className="btn-text"
                      disabled={busy !== null}
                      onClick={() => {
                        void onReview(row.id, 'dismiss');
                      }}
                      style={{ padding: '6px 10px', fontSize: 12, color: 'var(--rose)' }}
                    >
                      رفض
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
